import { ITTypeQuantityDisplay } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { InputTableLayoutStepProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { ColumnOrderTableSchema, OrderData } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../specificComponents/Product/types'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import { getAllSpecificPriceRules, getUserPermissions } from '../../../../../../../../../utils/reducers/getters'
import { CustomRenderString } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import AdjustmentDeliveryNoteFinishedStepHeaderDecider from './AdjustmentDNFinishedStepHeader'
import AdjustmentDeliveryNoteFinishedStepITHeader from './AdjustmentDNFinishedStepITHeader'
import { AdjustmentDeliveryNoteStatusInversed } from '@teinor/erp/types/company/order/adjustmentDeliveryNote'
import useOrderRowRenderer from '../../../../../../../../../utils/hooks/orders/useOrderRowRenderer'
import { CustomRenderPriceWithMargin, CustomRenderSpecificPrice } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/CustomRenders'
import { AdjustmentDeliveryNoteProductModelTypeExtended } from '@teinor/erp/types/company/order/adjustmentDeliveryNote/adjustmentDeliveryNoteProduct'
import useAdjustmentDeliveryNoteHeaderURLs from '../../../../../../../../../utils/hooks/adjustmentDeliveryNote/useAdjustmentDeliveryNoteHeaderURLs'
import OrderAdjustmentDeliveryNoteLayout from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderAdjustmentDeliveryNoteLayout'

interface AdjustmentDeliveryNoteFinishedStepProps extends InputTableLayoutStepProps { }

const AdjustmentDeliveryNoteFinishedStep: React.FC<AdjustmentDeliveryNoteFinishedStepProps> = ({ inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges }) => {
    const { t } = useTwinTranslation()
    const { rowRenderer } = useAdjustmentDeliveryNoteFinishedStepLogic({ completeData })
    const orderHeaderURLs = useAdjustmentDeliveryNoteHeaderURLs()
    const specificPriceRules = getAllSpecificPriceRules()

     if (!completeData) {
        return null
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & AdjustmentDeliveryNoteProductModelTypeExtended> = [
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            simpleLabel: t('codeBar', 'Código de barras'),
            component: 'OrderSearchProductInputCreate',
            field: 'code_bar',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true,
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchProductInput',
            field: 'reference',
            sortable: true,
            minWidth: 100,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchProductInputName',
            field: 'name',
            sortable: true,
            hideColumnByDefault: true,
            className: 'quote_order_reference_name'
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            minWidth: 180,
            className: 'quote_order_reference_name'
        },
        {
            id: 'unique_number',
            dataKey: 'unique_number',
            label: t('uniqueNumberShort', 'Nº único'),
            simpleLabel: t('uniqueNumber', 'Número único'),
            toolTip: t('uniqueNumber', 'Número único'),
            component: 'OrderUniqueNumberProductReadOnly',
            hideColumnByDefault: true,
        },
        {
            id: 'editProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonSeeProduct', 'Botón de ver producto'),
            component: 'OrderEditProduct',
            width: 35,
            havePermission: permissionCheck(getUserPermissions('product.profile.main'), 'read'),
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} />
        },
        {
            id: 'initialUnitPriceWithoutTax',
            dataKey: 'initialUnitPriceWithoutTax',
            label: () => <div className='text-right mr-15 w-full'>{t('price', 'Precio')}</div>,
            simpleLabel: t('price', 'Precio'),
            component: 'ITDisplayPrice',
            width: 100,
        },
        {
            id: 'Product.SpecificPriceRuleId',
            dataKey: 'Product.SpecificPriceRuleId',
            label: (props) => {
                const myValue = parseInt(props.selectedFilterValue || props.value || specificPriceRules?.defaultSpecificPriceRuleId)
                return <span>{t('pvp', 'PVP') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
            },
            simpleLabel: t('sellPrice', 'Precio de venta'),
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
            },
            customRender: (parameterValue, allRowData) => <CustomRenderSpecificPrice specificPriceRules={specificPriceRules} allRowData={allRowData} />,
            hideColumnByDefault: true
        },
        {
            id: 'Product.SpecificPriceRuleIdActual',
            dataKey: 'Product.SpecificPriceRuleId',
            label: t('costPriceWithMarginShort', 'PCM'),
            simpleLabel: t('costPriceWithMargin', 'Precio coste con margen'),
            toolTip: t('costPriceWithMargin', 'Precio coste con margen'),
            customRender: (parameterValue, allRowData) => <CustomRenderPriceWithMargin allRowData={allRowData} CurrencyId={completeData?.header?.CurrencyId} />,
            hideColumnByDefault: true
        },
        {
            id: 'margin',
            dataKey: 'margin',
            label: t('margin', 'Margen'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            hideColumnByDefault: true,
            width: 100,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            width: 110,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity2} value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            width: 100,
            hideColumnByDefault: true,
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            width: 110,
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'rowPriceWithoutTaxPrev',
            dataKey: 'rowPriceWithoutTaxPrev',
            label: () => <div className='text-right mr-15 w-full'>{t('rowPrice', 'Precio Línea')}</div>,
            simpleLabel: t('rowPrice', 'Precio Línea'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 120,
            hideColumnByDefault: true
        },
        {
            id: 'rowPriceWithoutTaxAndTotalDiscount',
            dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('priceTotal', 'Precio Total')}</div>,
            simpleLabel: t('priceTotal', 'Precio Total'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 120,
        },
        {
            id: 'rowPriceWithTaxAndTotalDiscount',
            dataKey: 'rowPriceWithTaxAndTotalDiscount',
            label: () => <div className='text-right w-full'>{t('totalPriceWithTaxShort', 'Precio Total Imp.')}</div>,
            simpleLabel: t('totalPriceWithTaxShort', 'Precio Total Imp.'),
            component: 'ITDisplayFinalPrice',
            with_vat: false,
            width: 130,
            hideColumnByDefault: true,
        },
        {
            id: 'totalWithTaxWithComplements',
            dataKey: 'totalWithTaxWithComplements',
            simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
            label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
            component: 'ITDisplayPrice',
            width: 160,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
        },
        {
            id: 'totalWithoutTaxWithComplements',
            dataKey: 'totalWithoutTaxWithComplements',
            simpleLabel: t('totalWithComplements', 'Total con complementos'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithComplements', 'Total con complementos')
        },
        {
            id: 'totalWithoutTaxWithComplementsPrev',
            dataKey: 'totalWithoutTaxWithComplementsPrev',
            simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
            label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
        },
        {
            id: 'unitPriceWithComplements',
            dataKey: 'unitPriceWithComplements',
            simpleLabel: t('unitPriceWithComplements', 'Precio unitario con complementos'),
            label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
            component: 'ITDisplayPrice',
            width: 110,
            hideColumnByDefault: true,
            toolTip: t('unitPriceWithComplements', 'Precio unitario con complementos'),
        },
        {
            id: 'WarehouseId',
            dataKey: 'WarehouseId',
            label: t('warehouse', 'Almacén'),
            customRender: (parameterValue) => <CustomRenderString value={completeData?.header?.Warehouse?.name || ''} />,
        },
        {
            id: 'PrintTags',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('printTags', 'Imprimir etiquetas'),
            width: 33,
            component: 'OrderPrintTags',
            hideColumnByDefault: true
        },
    ]
    return (
        <OrderAdjustmentDeliveryNoteLayout modelId='AdjustmentDeliveryNoteId' name='AdjustmentDeliveryNoteFinishedStep' translations={{ headerTitle: t('adjustmentDeliveryNote', 'Albarán de regularización') }} buttonSave={
            <AdjustmentDeliveryNoteFinishedStepHeaderDecider orderStatus={completeData?.header.status} OrderLogs={completeData?.header?.OrderLogs} onSubmit={getCompleteData} data={completeData as OrderData} isReadOnly={true} {...{ orderHeaderURLs, setCompleteData, updateHeaderData: inputTableURLs?.updateHeaderData, userPermissions }} />
        } InputTableHeaderChildren={AdjustmentDeliveryNoteFinishedStepITHeader} isReadOnly={true} columns={columns} {...{ rowRenderer, inputTableURLs, orderHeaderURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges, orderStatusDict: AdjustmentDeliveryNoteStatusInversed, hideFinancialRows: true }} />
    )
}

interface AdjustmentDeliveryNoteFinishedStepLogicProps {
    completeData: OrderData | null
}

export const useAdjustmentDeliveryNoteFinishedStepLogic = ({ completeData }: AdjustmentDeliveryNoteFinishedStepLogicProps) => {
    const rowRenderer = useOrderRowRenderer({ completeData, isFinishedStep: true })

    return { rowRenderer }
}



export default AdjustmentDeliveryNoteFinishedStep